// Servomotores: SG90 (9 g, engranes de plástico) y MG996R (55 g, engranes metálicos).
import { definePart, PITCH } from './registry.js';

// Medidas en mm de cada modelo. body: cuerpo sin orejas (x0/x1 extremos, w ancho en z, h alto hasta la tapa),
// ear: orejas de montaje (largo total, altura de su cara inferior y grosor), shaftX: posición del eje de
// salida y connectorX: punta del cable (acortado) con su conector hembra. SG90: 22.2 × 11.8 × 31 mm de la hoja
// de TowerPro; MG996R: 40.7 × 19.7 × 42.9 mm de su hoja de datos.
export const SERVO_MODELS = {
  sg90: {
    name: 'SG90',
    body: { x0: -11.4, x1: 11.4, w: 12.2, h: 22.7 },
    ear: { len: 32.2, y: 15.9, t: 2.5 },
    shaftX: 5.9,
    connectorX: -44,
  },
  mg996r: {
    name: 'MG996R',
    body: { x0: -20.35, x1: 20.35, w: 19.7, h: 36.5 },
    ear: { len: 53.8, y: 26.7, t: 2.5 },
    shaftX: 10.1,
    connectorX: -62,
  },
};

/** Medidas del modelo indicado en las propiedades (el SG90 si no se indica). */
export function servoModel(props) {
  return SERVO_MODELS[props && props.model] || SERVO_MODELS.sg90;
}

export const SERVO_LOADS = [
  { value: 'none', label: 'Sin carga' },
  { value: 'light', label: 'Ligera (≈ 25 % del par máximo)' },
  { value: 'heavy', label: 'Pesada (≈ 70 %)' },
  { value: 'blocked', label: 'Bloqueado' },
];

definePart({
  type: 'servo',
  name: 'Servomotor (SG90 o MG996R)',
  category: 'actuadores',
  description: 'Gira de 0° a 180° según el ancho del pulso (librería Servo). Café = GND, rojo = 5 V, naranja = señal. Consume corriente de V+ según lo que mueva.',
  keywords: 'servo motor sg90 mg90 mg996r angulo par fuerza carga',
  props: [
    { key: 'model', label: 'Modelo', type: 'select', default: 'sg90', options: [
      { value: 'sg90', label: 'SG90 (9 g, plástico)' },
      { value: 'mg996r', label: 'MG996R (55 g, metal)' },
    ] },
    { key: 'horn', label: 'Brazo', type: 'select', default: 'single', options: [{ value: 'single', label: 'Simple' }, { value: 'double', label: 'Doble' }, { value: 'cross', label: 'Cruz' }] },
    { key: 'load', label: 'Carga en el brazo', type: 'select', default: 'none', runtime: true, options: SERVO_LOADS },
  ],
  variants: [
    { label: 'Servomotor SG90', props: { model: 'sg90' } },
    { label: 'Servomotor MG996R', props: { model: 'mg996r' } },
  ],
  pins: (props) => {
    const m = servoModel(props);
    return ['PWM', 'V+', 'GND'].map((name, i) => ({
      name,
      label: { PWM: 'Señal (naranja)', 'V+': 'V+ 5 V (rojo)', GND: 'GND (café)' }[name],
      kind: name === 'GND' ? 'gnd' : name === 'V+' ? 'power' : 'signal',
      x: m.connectorX,
      y: 1.3,
      z: (i - 1) * PITCH,
      dir: [-1, 0, 0],
    }));
  },
  footprint: (props) => {
    const m = servoModel(props);
    const x0 = m.connectorX - 2;
    const x1 = m.ear.len / 2;
    const d = m.body.w + 2;
    return { w: x1 - x0, d: Math.max(14, d), h: m.body.h + 8, x: (x0 + x1) / 2 };
  },
  help: 'Alimenta V+ con 4.8–6 V (el MG996R, hasta 7.2 V) y une GND con el de la placa. Toma de V+ la corriente que tomaría el real: 10 mA en reposo y, ' +
    'moviéndose sin carga, ≈ 100 mA el SG90 (≈ 480 mA el MG996R) y más con carga; si el brazo no puede girar, la corriente de atasco ' +
    '(≈ 550 mA el SG90 y 2 A el MG996R con 4.8 V; 2.5 A con 6 V). Con menos voltaje gira más lento y con menos fuerza: con la carga «Pesada» ' +
    'y poco voltaje ya no llega a moverse. Sin pulsos (detach) suelta el motor. No se simula el pico de arranque de unos milisegundos ni el calentamiento.',
});
